<template>
  <div class="app-container dashboard-page" v-loading="loading">
    <el-row :gutter="16">
      <el-col v-for="c in cards" :key="c.title" :xs="12" :sm="8" :md="4">
        <el-card shadow="never" class="stat-card">
          <div class="stat-title">{{ c.title }}</div>
          <div class="stat-value">
            <i :class="c.icon" class="stat-icon" :style="{ color: c.color }"></i>
            <span>{{ c.value }}</span>
            <span v-if="c.suffix" class="stat-suffix">{{ c.suffix }}</span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="block-card">
      <div slot="header" class="card-header">
        <span>趋势分析</span>
        <el-radio-group v-model="days" size="small" @change="loadTrend(days)">
          <el-radio-button v-for="o in RANGE_OPTIONS" :key="o.value" :label="o.value">{{ o.label }}</el-radio-button>
        </el-radio-group>
      </div>
      <TrendChart :data="trend" />
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header" class="card-header">
        <span>客服绩效</span>
        <div class="perf-controls">
          <el-radio-group v-model="perfPreset" size="small" @change="handlePerfPresetChange">
            <el-radio-button v-for="o in PERF_PRESETS" :key="o.value" :label="o.value">{{ o.label }}</el-radio-button>
          </el-radio-group>
          <el-date-picker
            v-if="perfPreset === 'custom'"
            v-model="perfRangeValue"
            type="daterange"
            size="small"
            value-format="yyyy-MM-dd"
            :clearable="false"
            @change="handleCustomRange"
          />
        </div>
      </div>

      <el-table :data="perf" row-key="agentId">
        <el-table-column prop="nickname" label="客服" width="120" />
        <el-table-column label="状态" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.onlineStatus)" size="small">{{ statusLabel(row.onlineStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="activeSessions" label="服务中" width="80" />
        <el-table-column prop="rangeSessions" :label="rangeLabel + '接线'" width="110" />
        <el-table-column prop="totalSessions" label="累计接线" width="90" />
        <el-table-column :label="rangeLabel + '评分'" width="220">
          <template slot-scope="{ row }">
            <template v-if="row.ratingCount > 0">
              <el-rate :value="row.avgRating" disabled allow-half size="small" class="perf-rate" />
              <span class="perf-rate-count">({{ row.ratingCount }})</span>
            </template>
            <span v-else class="perf-no-rate">暂无</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import TrendChart from './components/TrendChart'
import { getOverviewStats, getTrendStats, getAgentPerformance } from '@/api/customerService/stats'

const RANGE_OPTIONS = [
  { label: '7 天', value: 7 },
  { label: '14 天', value: 14 },
  { label: '30 天', value: 30 }
]

const PERF_PRESETS = [
  { label: '今日', value: 'today' },
  { label: '近 7 天', value: '7d' },
  { label: '近 30 天', value: '30d' },
  { label: '自定义', value: 'custom' }
]

function pad (n) {
  return n < 10 ? '0' + n : '' + n
}

function getRangeFromPreset (preset) {
  const today = new Date()
  switch (preset) {
    case 'today':
      return [fmt(today), fmt(today)]
    case '7d':
      return [fmt(new Date(today.getTime() - 6 * 24 * 3600 * 1000)), fmt(today)]
    case '30d':
      return [fmt(new Date(today.getTime() - 29 * 24 * 3600 * 1000)), fmt(today)]
    default:
      return [fmt(new Date(today.getTime() - 6 * 24 * 3600 * 1000)), fmt(today)]
  }
}

function fmt (d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'StatsList',
  components: { TrendChart },
  data () {
    const preset = 'today'
    const range = getRangeFromPreset(preset)
    return {
      RANGE_OPTIONS,
      PERF_PRESETS,
      stats: null,
      trend: [],
      perf: [],
      loading: true,
      days: 7,
      perfPreset: preset,
      perfRange: range,
      perfRangeValue: [...range],
      overviewTimer: null,
      CARD_DEFS: [
        { title: '今日会话', key: 'todaySessions', suffixKey: 'totalSessions', icon: 'el-icon-chat-dot-round', color: '#4f46e5' },
        { title: '进行中', key: 'activeSessions', icon: 'el-icon-time', color: '#10b981' },
        { title: '今日消息', key: 'todayMessages', suffixKey: 'totalMessages', icon: 'el-icon-message', color: '#f59e0b' },
        { title: '今日 AI 调用', key: 'todayAiCalls', icon: 'el-icon-magic-stick', color: '#8b5cf6' },
        { title: '在线客服', key: 'onlineAgents', icon: 'el-icon-user', color: '#06b6d4' },
        { title: '待处理工单', key: 'openTickets', icon: 'el-icon-tickets', color: '#ef4444' }
      ]
    }
  },
  computed: {
    // 将接口返回的 stats 映射到卡片 value/suffix，字段为空显示 '--'
    cards () {
      return this.CARD_DEFS.map(def => {
        const value = this.stats ? this.stats[def.key] : undefined
        const suffix = def.suffixKey && this.stats ? this.stats[def.suffixKey] : undefined
        return {
          ...def,
          value: value == null || value === '' ? '--' : value,
          suffix: suffix == null || suffix === '' ? undefined : suffix
        }
      })
    },
    rangeLabel () {
      if (this.perfPreset === 'today') return '今日'
      if (this.perfPreset === 'custom') {
        return `${this.perfRange[0].slice(5)} - ${this.perfRange[1].slice(5)}`
      }
      if (this.perfPreset === '7d') return '近 7 天'
      return '近 30 天'
    }
  },
  watch: {
    days (v) {
      this.loadTrend(v)
    },
    perfRange () {
      this.loadPerf()
    }
  },
  created () {
    Promise.all([this.loadOverview(), this.loadTrend(this.days), this.loadPerf()]).then(() => {
      this.loading = false
    })
    this.overviewTimer = window.setInterval(this.loadOverview, 30000)
  },
  beforeDestroy () {
    if (this.overviewTimer != null) {
      clearInterval(this.overviewTimer)
    }
  },
  methods: {
    async loadOverview () {
      try {
        const res = await getOverviewStats()
        this.stats = res.result || {}
      } catch (e) { /* ignore */ }
    },
    async loadTrend (d) {
      try {
        const res = await getTrendStats(d)
        this.trend = res.result || []
      } catch (e) { /* ignore */ }
    },
    async loadPerf () {
      try {
        const res = await getAgentPerformance({
          startDate: this.perfRange[0],
          endDate: this.perfRange[1]
        })
        this.perf = res.result || []
      } catch (e) { /* ignore */ }
    },
    handlePerfPresetChange (v) {
      const p = String(v)
      if (p !== 'custom') {
        this.perfRange = getRangeFromPreset(p)
        this.perfRangeValue = [...this.perfRange]
      }
    },
    handleCustomRange (val) {
      if (val && val[0] && val[1]) {
        this.perfRange = [val[0], val[1]]
      }
    },
    statusTagType (status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      return 'info'
    },
    statusLabel (status) {
      const map = { 1: '在线', 2: '忙碌', 0: '离线' }
      return map[Number(status)] || '离线'
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  min-height: 200px;
}

.stat-card ::v-deep  .el-card__body {
  padding: 16px;
}

.stat-title {
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 24px;
  font-weight: 600;
}

.stat-icon {
  align-self: center;
  font-size: 20px;
}

.stat-suffix {
  font-size: 13px;
  color: #909399;
  font-weight: 400;
}

.block-card {
  margin-top: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.perf-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.perf-rate {
  display: inline-block;
  vertical-align: middle;
}

.perf-rate-count {
  color: #8c8c8c;
  font-size: 12px;
  margin-left: 4px;
}

.perf-no-rate {
  color: #8c8c8c;
}
</style>